import { useEffect, useMemo } from "react";

import { DEFAULT_DRAFT_COLORS } from "../redux/constants";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { setDraftColors, setTeamTheme, useGetUser } from "../redux/slices";
import { useLazyGetTeamByIdQuery } from "../redux/slices/team-api";

export const useSetTheme = () => {
  const dispatch = useAppDispatch();

  const [getTeamById, team] = useLazyGetTeamByIdQuery();

  const theme = useAppSelector((state) => state.theme);
  const { user } = useGetUser();
  const userDetails = user?.userDetails;
  const accountType = userDetails?.AccountType;
  const accountProperties = useMemo(() => user?.products?.FASTDRAW2?.[0]?.account?.properties, [user]);
  const teamId = accountProperties?.find((property) => property.key === "team_id")?.value;

  /** ======== Get Team By Id ======== */
  useEffect(() => {
    if (accountType === "enterprise" && teamId) getTeamById({ id: teamId });
  }, [accountType, teamId, getTeamById]);

  /** ======== Set Draft Colors ======== */
  useEffect(() => {
    const draftColors =
      accountProperties?.find((property) => property.key === "draftColors")?.value?.split(",") || DEFAULT_DRAFT_COLORS;

    dispatch(setDraftColors({ draftColors }));
  }, [accountProperties, dispatch]);

  /** ======== Set Team Theme ======== */
  useEffect(() => {
    if (accountType === "consumer") {
      const teamColor = accountProperties?.find((property) => property.key === "primary_color")?.value || "";
      const teamLogo = accountProperties?.find((property) => property.key === "team_logo")?.value || "";
      const teamName = accountProperties?.find((property) => property.key === "team_name")?.value || "";
      const teamNickName = accountProperties?.find((property) => property.key === "team_nick")?.value || "";

      dispatch(setTeamTheme({ teamColor, teamLogo, teamName, teamNickName }));
    } else if (accountType === "enterprise" && team.isSuccess) {
      const teamData = team.data;
      const { colorPrimary: teamColor, imageUrl: teamLogo, name: teamName, nick: teamNickName } = teamData;
      dispatch(setTeamTheme({ teamColor, teamLogo, teamName, teamNickName }));
    }
  }, [accountProperties, accountType, dispatch, team]);

  /** ======== Set CSS Properties ======== */
  useEffect(() => {
    const element = document.documentElement;
    if (element !== null) {
      element.style.setProperty("--team-color", `#${theme.teamColor || "3358A7"}`); // if no team color, default to FastDraw blue
      element.style.setProperty("--team-logo", `url(${theme.teamLogo})`);
    }
  }, [theme]);
};
